import { first, get, isEmpty, isUndefined, omitBy } from 'lodash';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React from 'react';

import { getFeatureFlags } from '@utils/feature-flags';
import { assetQuery, floodlightQuery, seomaticQuery } from '../../content/query-snippets';
import { Col, Container, Row } from '../../components/grid';
import { ContentMatrix } from '../../components/content-matrix';
import { ContentSubHeader, PageHeaderBlock } from '../../components/content-image';
import { DownloadButton } from '../../components/download-button';
import { extractSeoMetaFromEntry, seoPropDefaults, seoPropTypes } from '../../utils/extract-seo-meta-from-entry';
import { fetch } from '../../content/queries';
import { floodlightPageLoadEventPropTypes, recipePropType } from '../../prop-types';
import { imageTransform } from '../../content/transforms';
import { Layout } from '../../components/layout';
import { logger } from '../../utils/logger';
import { navigation } from '../../constants';
import { P } from '../../components/typography';
import { RecipeIngredients } from '../../components/recipe-ingredients';
import { RecipeMethod } from '../../components/recipe-method';
import { RecipeTimes } from '../../components/recipe-times';
import { setCacheHeaders } from '../../utils/set-cache-headers';
import { Video } from '../../components/video';
import Error from '../_error';

const RecipePage = ({ metaContent, recipe, statusCode, floodlightPageLoadEvent }) => {
  if (statusCode) {
    return <Error statusCode={statusCode} />;
  }

  const {
    contentMatrix,
    cookTime,
    description,
    instructions,
    method,
    pageHeaderImage,
    prepTime,
    recipeIngredients,
    servingSize,
    title,
    video,
  } = recipe;

  const { autoPlay, isIframeVideo, poster, source, title: videoTitle } = first(video) || {};

  const breadcrumbs = {
    title,
    ancestors: [
      {
        title: 'Recipes',
        url: navigation.RECIPES,
      },
    ],
  };

  return (
    <Layout title={title} hasNavPadding metaContent={metaContent} floodlightPageLoadEvent={floodlightPageLoadEvent}>
      <PageHeaderBlock backgroundImage={pageHeaderImage} />
      <ContentSubHeader {...{ title, breadcrumbs }}>
        {description && <P mb={1}>{description}</P>}
        <RecipeTimes prepTime={prepTime} cookTime={cookTime} />
      </ContentSubHeader>
      <Container flexDirection="column" flex={1}>
        {!isEmpty(video) && (
          <Row mb={2}>
            <Col width={{ xs: 12 / 12, md: 8 / 12 }} offset={[0, 0, 2 / 12]}>
              <Video
                autoPlay={autoPlay}
                mimeType={isIframeVideo ? 'text/html' : 'video/mp4'}
                poster={poster ? imageTransform(poster) : null}
                src={source}
                title={videoTitle}
                useAspectRatio
              />
            </Col>
          </Row>
        )}
        {!isEmpty(instructions) && (
          <DownloadButton
            buttonText="Download Recipe"
            heading="[Love this recipe? Download it]"
            variant="secondary"
            href={first(instructions).url}
          />
        )}
        <Row flexDirection={{ xs: 'column', lg: 'row' }}>
          <Col width={{ xs: 12 / 12, lg: 4 / 12 }} mb={1}>
            <RecipeIngredients recipeIngredients={recipeIngredients} servingSize={servingSize} />
          </Col>
          <Col width={{ xs: 12 / 12, lg: 8 / 12 }}>
            <RecipeMethod method={method} />
          </Col>
        </Row>
      </Container>
      <ContentMatrix blocks={contentMatrix} />
    </Layout>
  );
};

RecipePage.getInitialProps = async ({ query, res }) => {
  const preview = get(query, 'x-craft-live-preview');
  const token = get(query, 'token');
  const slug = get(query, 'slug', '');
  const featureFlags = await getFeatureFlags();

  const recipesQuery = gql`
      query getRecipeFromCMS {
        entry(section: "recipes", slug: "${slug}", siteId: "${process.env.cms.siteId}") {
          ... on recipe_Entry {
            slug
            title
            description
            recipeCategory {
              title
            }
            video {
              ... on videos_Entry {
                ${assetQuery('poster')}
                title
                autoPlay
                isIframeVideo
                source
              }
            }
            instructions {
              url
            }
            servingSize
            method {
              ... on methodBlock_Entry {
                step
                image {
                  url
                  focalPoint
                  ...on assets_Asset {
                    alt
                  }
                }
              }
            }
            recipeIngredients {
              ... on recipeIngredientsBlock_Entry {
                ingredient
                quantity
              }
            }
            cookTime
            prepTime
            ${assetQuery('pageHeaderImage')}
            ${ContentMatrix.query}
            ${floodlightQuery}
          }
          ${seomaticQuery(`recipes/${slug}`)}
        }
      }
    `;

  try {
    const data = await fetch({
      query: recipesQuery,
      headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
    });

    const recipe = get(data, 'entry', []);
    const floodlightPageLoadEvent = get(recipe, 'floodlightPageLoadEvent[0]');
    const metaContent = extractSeoMetaFromEntry(recipe);

    if (res) {
      if (!recipe) {
        // eslint-disable-next-line no-param-reassign
        res.statusCode = 404;
        return { statusCode: 404, featureFlags };
      }

      const isPreview = preview && token;
      setCacheHeaders({ res, isPreview });
    }

    return { metaContent, recipe, floodlightPageLoadEvent, featureFlags };
  } catch (err) {
    logger.error('Failed to fetch recipes from CMS', err);
    return { statusCode: 500, featureFlags };
  }
};

RecipePage.propTypes = {
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: PropTypes.shape(seoPropTypes),
  recipe: recipePropType.isRequired,
  statusCode: PropTypes.number,
};

RecipePage.defaultProps = {
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
  statusCode: null,
};

export default RecipePage;
